"use client";

/**
 * The Name section's single-line motif, built as one continuous journey
 * (homepage revision brief, section 02 design note).
 *
 * A delicate line enters from the whitespace of the previous section,
 * crosses itself four times as it passes the eyebrow (four strands), runs
 * on past the copy, and resolves into a small drawn S: the first moment of
 * the line whose second moment is the giant serif S watermark before The
 * Protocol. The pieces (entry stroke, knot, exit stroke, S) are chained
 * into one scroll-scrubbed draw so the line appears to travel through the
 * section as you read it.
 *
 * Layout is slot-based so the surrounding server component keeps ownership
 * of the copy; this component only owns the drawn line and the timeline.
 *
 * Fallbacks: no JS or prefers-reduced-motion leaves every piece fully
 * drawn (the initial styles are the finished state; hidden states are only
 * introduced inside the effect once motion is allowed).
 */
import { useRef, type ReactNode } from "react";
import { gsap, useGSAP, prefersReducedMotion } from "./gsap";

const STROKE = "var(--color-gold)";

export interface WovenLineProps {
  eyebrow: ReactNode;
  heading: ReactNode;
  body: ReactNode;
  cta: ReactNode;
  className?: string;
}

export function WovenLine({
  eyebrow,
  heading,
  body,
  cta,
  className = "",
}: WovenLineProps) {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || prefersReducedMotion()) return;

      const entry = el.querySelector<HTMLElement>("[data-line-entry]");
      const exit = el.querySelector<HTMLElement>("[data-line-exit]");
      const paths = Array.from(
        el.querySelectorAll<SVGPathElement>("[data-line-draw]")
      );

      // Dash setup: each path hides behind its own full length.
      paths.forEach((p) => {
        const len = p.getTotalLength();
        p.style.strokeDasharray = `${len}`;
        p.style.strokeDashoffset = `${len}`;
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 82%",
          end: "bottom 55%",
          scrub: 1,
        },
      });

      // One journey: entry stroke, then the four-crossing knot, then the
      // exit stroke, resolving as the line draws the small S itself.
      // Durations are weighted by visual length.
      if (entry) {
        tl.fromTo(
          entry,
          { scaleY: 0, transformOrigin: "top center" },
          { scaleY: 1, ease: "none", duration: 0.18 }
        );
      }
      const [knot, s] = paths;
      if (knot) {
        tl.to(knot, { strokeDashoffset: 0, ease: "none", duration: 0.42 });
      }
      if (exit) {
        tl.fromTo(
          exit,
          { scaleY: 0, transformOrigin: "top center" },
          { scaleY: 1, ease: "none", duration: 0.22 }
        );
      }
      if (s) {
        tl.to(s, { strokeDashoffset: 0, ease: "none", duration: 0.18 });
      }
    },
    { scope: root }
  );

  return (
    <div ref={root} className={`flex flex-col items-center ${className}`}>
      {/* Entry: the line drops out of the previous section's whitespace. */}
      <span
        data-line-entry
        aria-hidden="true"
        className="block h-14 w-px sm:h-20"
        style={{ backgroundColor: STROKE, opacity: 0.65 }}
      />

      <div className="mt-8">{eyebrow}</div>

      {/* Knot: one continuous stroke descending the same centre axis as
          the entry line, curling left and right as it falls and crossing
          itself four times (four strands) before running on. */}
      <svg
        aria-hidden="true"
        className="mt-8 w-[60px]"
        viewBox="0 0 60 96"
        fill="none"
      >
        <path
          data-line-draw
          d="M30 0 V12 c 26,28 26,-12 0,14 c -26,28 -26,-12 0,14 c 26,28 26,-12 0,14 c -26,28 -26,-12 0,14 V96"
          stroke={STROKE}
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.75"
        />
      </svg>

      <div className="mt-6">{heading}</div>
      {body}
      {cta}

      {/* Exit: the line runs on below the copy... */}
      <span
        data-line-exit
        aria-hidden="true"
        className="mt-10 block h-12 w-px sm:h-16"
        style={{ backgroundColor: STROKE, opacity: 0.65 }}
      />

      {/* ...and resolves by forming the motif itself: a small S drawn in
          a single stroke (the monogram as line, per review: no crest
          artwork here, the line forms the shape). */}
      <svg
        aria-hidden="true"
        className="-mt-px w-7 sm:w-8"
        viewBox="0 0 40 56"
        fill="none"
      >
        {/* Path mirrored about x=20 (viewBox width 40): the previous
            version bulged right at the top, drawing a reversed S. */}
        <path
          data-line-draw
          d="M20 0 C20 6 9 4 7 12 C5 20 18 22 25 26 C34 31 35 40 27 45 C19 50 8 46 9 38"
          stroke={STROKE}
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.75"
        />
      </svg>
    </div>
  );
}
